import * as React from 'react';

/**
 * Tracks whether a horizontally scrolling container overflows and whether it
 * is scrolled to its leading or trailing edge (direction-agnostic: RTL
 * scroll offsets are negative, so the magnitude is used). Scroll containers use it to become
 * keyboard-focusable and to fade their trailing edge only while there is more
 * to see (Table, SubNav). Re-measures on scroll and on resize of the
 * container or its first child.
 */
export function useHorizontalOverflow(ref: React.RefObject<HTMLElement | null>): {
  overflowing: boolean;
  atStart: boolean;
  atEnd: boolean;
} {
  const [state, setState] = React.useState({ overflowing: false, atStart: true, atEnd: true });

  React.useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const measure = (): void => {
      const overflowing = element.scrollWidth > element.clientWidth + 1;
      const scrolled = Math.abs(element.scrollLeft);
      const atStart = !overflowing || scrolled <= 1;
      const atEnd = !overflowing || scrolled + element.clientWidth >= element.scrollWidth - 1;
      setState((previous) =>
        previous.overflowing === overflowing &&
        previous.atStart === atStart &&
        previous.atEnd === atEnd
          ? previous
          : { overflowing, atStart, atEnd },
      );
    };

    measure();
    element.addEventListener('scroll', measure, { passive: true });
    const observer =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
    observer?.observe(element);
    const content = element.firstElementChild;
    if (content) observer?.observe(content);

    return () => {
      element.removeEventListener('scroll', measure);
      observer?.disconnect();
    };
  }, [ref]);

  return state;
}
